// All access to the browser Notification API lives here. The in-app IncomingView
// modal is the source of truth; OS notifications are a best-effort attention
// signal for when the lobby tab is open but unfocused. Desktop only — mobile
// degrades to a silent no-op.

export function notificationsSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'Notification' in window &&
    typeof window.Notification === 'function'
  );
}

export function getPermission(): NotificationPermission {
  return notificationsSupported() ? window.Notification.permission : 'denied';
}

export async function requestPermission(): Promise<NotificationPermission> {
  if (!notificationsSupported()) return 'denied';
  try {
    return await window.Notification.requestPermission();
  } catch {
    // Some browsers throw instead of resolving 'denied' when called without a
    // user gesture. Treat that as a denial; never surface an error.
    return 'denied';
  }
}

export function shouldShowEnablePrompt(args: {
  supported: boolean;
  permission: NotificationPermission;
}): boolean {
  return args.supported && args.permission === 'default';
}

const INCOMING_TAG = 'aslgo-incoming';
let current: Notification | null = null;

export function showIncoming(fromName: string, topic?: string | null): void {
  if (!notificationsSupported()) return;
  if (window.Notification.permission !== 'granted') return;
  // Only surface an OS notification when the guide isn't looking at the lobby.
  // hasFocus() is false for a background tab, a minimized window, or another app.
  if (typeof document !== 'undefined' && document.hasFocus()) return;
  try {
    dismissIncoming();
    const n = new window.Notification('Incoming session request', {
      body: topic
        ? `${fromName} wants to start a session • ${topic}`
        : `${fromName} wants to start a session`,
      icon: '/favicon.svg',
      tag: INCOMING_TAG,
      requireInteraction: true,
    });
    n.onclick = () => {
      try { window.focus(); } catch { /* ignore */ }
      n.close();
    };
    current = n;
  } catch {
    // Best-effort: the in-app IncomingView modal is the source of truth.
  }
}

export function dismissIncoming(): void {
  if (!current) return;
  try { current.close(); } catch { /* ignore */ }
  current = null;
}

/** A user-initiated test notification. Deliberately does NOT check
 *  document.hasFocus(): the guide has just clicked a button, so the page always
 *  has focus, and the suppression showIncoming relies on would swallow every
 *  test. Returns whether a notification was actually constructed — false covers
 *  unsupported browsers, ungranted permission, and the Android Chrome case
 *  where the constructor throws. */
export function showTest(): boolean {
  if (!notificationsSupported()) return false;
  if (window.Notification.permission !== 'granted') return false;
  try {
    new window.Notification('ASL Go test notification', {
      body: "Desktop notifications are working. You'll see one like this when a learner calls.",
      icon: '/favicon.svg',
      tag: 'aslgo-test',
      // Matches showIncoming: a banner that auto-dismisses after a few seconds
      // would let a guide look away, see nothing, and still be told it worked.
      requireInteraction: true,
    });
    return true;
  } catch {
    return false;
  }
}
